<template>
  <div class="card" v-if="user.role === 'SUPER_ADMIN'">
    <Toast />
    <div class="title">Daftar Pegawai</div>

    <Toolbar class="p-mb-4">
      <template slot="left">
        <Button
          label="Tambah"
          icon="pi pi-plus"
          class="p-button-success"
          @click="create_dialog = true"
        />
      </template>
      <template slot="right"> </template>
    </Toolbar>

    <DataTable
      :value="employees"
      :lazy="true"
      :paginator="true"
      :rows="limit"
      :totalRecords="total_records"
      :loading="loading.filter"
      @page="getEmployees($event)"
    >
      <template #header>
        <div
          class="p-grid p-fluid p-mb-2"
          v-if="user && user.role == 'SUPER_ADMIN'"
        >
          <div class="p-col">
            <InputText v-model="search.username" placeholder="Nama" />
          </div>
          <div class="p-col">
            <InputText v-model="search.email" placeholder="Email" />
          </div>
          <AutoComplete
            class="p-col"
            placeholder="Outlet"
            :dropdown="true"
            :suggestions="outlets"
            v-model="search.outlet_selected"
            @complete="getOutlets()"
            field="code"
            optionLabel="code"
            v-if="user.outlet_id == 0"
          >
            <template #item="slotProps">
              <div class="p-col">
                <div>{{ slotProps.item.code }}</div>
              </div>
            </template>
          </AutoComplete>

          <AutoComplete
            class="p-col"
            placeholder="Warehouse"
            :dropdown="true"
            :suggestions="warehouses"
            v-model="search.warehouse_selected"
            @complete="getWarehouses()"
            field="name"
            optionLabel="name"
          >
            <template #item="slotProps">
              <div class="p-col">
                <div>{{ slotProps.item.name }}</div>
              </div>
            </template>
          </AutoComplete>
        </div>

        <div class="p-grid p-fluid p-justify-center">
          <div class="p-col-12 p-md-4 p-lg-4">
            <Button
              label="Cari"
              class="p-button-info"
              @click="getEmployees()"
              v-shortkey="['enter']"
              @shortkey="getEmployees()"
            />
          </div>
        </div>
      </template>
      <template #empty>Data tidak ada</template>
      <template #loading>Loading ...</template>

      <Column field="name" header="Nama" headerStyle="width: 7em">
        <template #body="slotProps">
          {{ slotProps.data.name }}
        </template>
      </Column>
      <Column field="email" header="Email" headerStyle="width: 10em">
        <template #body="slotProps">
          {{ slotProps.data.email }}
        </template>
      </Column>
      <Column
        field="phone"
        header="Phone"
        headerStyle="width: 10em; text-align: center;"
      >
        <template #body="slotProps">
          <div class="text-center">
            {{ slotProps.data.phone }}
          </div>
        </template>
      </Column>

      <Column field="roles" header="Role" headerStyle="width: 10em">
        <template #body="slotProps">
          {{ (slotProps.data.roles || []).join(', ') }}
        </template>
      </Column>
      <Column field="outlet" header="Outlet" headerStyle="width: 7em">
        <template #body="slotProps">
          <div v-if="slotProps.data.outlet_name">
            {{ slotProps.data.outlet_name }}
          </div>
          <div v-else>-</div>
        </template>
      </Column>
      <Column field="warehouse" header="Warehouse" headerStyle="width: 7em">
        <template #body="slotProps">
          <div v-if="slotProps.data.warehouse_name">
            {{ slotProps.data.warehouse_name }}
          </div>
          <div v-else>-</div>
        </template>
      </Column>
      <Column field="status" header="Status" headerStyle="width: 5.5em">
        <template #body="slotProps">
          <span
            v-if="slotProps.data.status === 1"
            class="p-text-bold"
            style="color: green"
            >Aktif</span
          >
          <span v-else class="p-text-bold" style="color: red">Tidak Aktif</span>
        </template>
      </Column>
      <Column headerStyle="width: 10em">
        <template #body="slotProps">
          <Button
            v-if="
              slotProps.data.status === 1 &&
              !(slotProps.data.roles || []).includes('SUPER_ADMIN')
            "
            icon="pi pi-pencil"
            class="p-button-rounded p-button-success"
            style="margin-right: 10px"
            @click="confirmCancelUpdate(slotProps.data)"
          ></Button>
          <Button
            v-if="
              slotProps.data.status === 1 &&
              !(slotProps.data.roles || []).includes('SUPER_ADMIN')
            "
            icon="pi pi-trash"
            class="p-button-rounded p-button-danger"
            @click="confirmCancel(slotProps.data)"
            :disabled="loading.delete"
          ></Button>
        </template>
      </Column>
    </DataTable>

    <Dialog
      :visible.sync="cancel_dialog"
      :style="{ width: '450px' }"
      header="Konfirmasi Pembatalan"
      :modal="true"
      class="p-fluid"
    >
      <div class="confirmation-content">
        Apakah kamu yakin ingin non-aktifkan shift <b>{{ cancel_data.name }}</b
        >?
      </div>

      <template #footer>
        <Button
          label="No"
          icon="pi pi-times"
          class="p-button-text"
          @click="cancel_dialog = false"
        />
        <Button
          label="Yes"
          icon="pi pi-check"
          class="p-button-text"
          @click="cancelShift()"
        />
      </template>
    </Dialog>

    <Dialog
      :visible.sync="create_dialog"
      :style="{ width: '450px' }"
      header="Tambah Pegawai"
      :modal="true"
      class="p-fluid"
    >
      <div class="p-field">
        <label>Role</label>
        <Dropdown
          v-model="selected_role"
          :options="roles"
          optionLabel="type"
          placeholder="Pilih role"
          @change="changeRole()"
        />
        <small class="p-error" v-if="is_error.role">{{ error_msg.role }}</small>
      </div>
      <div class="p-field" v-if="warehouse_mandatory">
        <label>Warehouse</label>
        <Dropdown
          v-model="selected_warehouse"
          :options="warehouses"
          optionLabel="code"
          placeholder="Pilih warehouse"
          @change="is_error.warehouse_id = false"
        />
        <small class="p-error" v-if="is_error.warehouse_id">{{
          error_msg.warehouse_id
        }}</small>
      </div>
      <div class="p-field" v-if="outlet_mandatory">
        <label>Outlet</label>
        <Dropdown
          v-model="selected_outlet"
          :options="outlets"
          optionLabel="code"
          placeholder="Pilih outlet"
          @change="is_error.outlet_id = false"
        />
        <small class="p-error" v-if="is_error.outlet_id">{{
          error_msg.outlet_id
        }}</small>
      </div>
      <div class="p-field">
        <label>Nama</label>
        <InputText
          required
          v-model="create_data.name"
          placeholder="Nama"
          @keyup.prevent="is_error.name = false"
        />
        <small class="p-error" v-if="is_error.name">{{ error_msg.name }}</small>
      </div>
      <div class="p-field">
        <label>Email</label>
        <InputText
          type="email"
          required
          pattern=".+@globex\.com"
          v-model="create_data.email"
          placeholder="Email"
          @keyup.prevent="is_error.email = false"
        />
        <small class="p-error" v-if="is_error.email">{{
          error_msg.email
        }}</small>
      </div>
      <div class="p-field">
        <label>Phone</label>
        <InputText
          type="number"
          placeholder="Phone"
          v-model="create_data.phone"
          required
          @keyup.prevent="is_error.phone = false"
        />
        <small class="p-error" v-if="is_error.phone">{{
          error_msg.phone
        }}</small>
      </div>
      <div class="p-field" v-if="password_field">
        <label>Password</label>
        <InputText
          required
          v-model="create_data.password"
          placeholder="Password"
          @keyup.prevent="is_error.password = false"
          type="password"
        />
        <small class="p-error" v-if="is_error.password">{{
          error_msg.password
        }}</small>
      </div>

      <template #footer>
        <Button
          label="Cancel"
          class="p-button-outlined"
          @click="create_dialog = false"
        />
        <Button label="Save" class="btn btn-primary" @click="createEmployee()">
          Save
          <span v-if="loading_save">
            <i class="pi pi-spin pi-spinner" style="font-size: 12px"></i>
          </span>
        </Button>
      </template>
    </Dialog>

    <!-- update employee -->
    <Dialog
      :visible.sync="update_dialog"
      :style="{ width: '450px' }"
      header="Edit Pegawai"
      :modal="true"
      class="p-fluid"
    >
      <div class="p-field">
        <label>Role</label>
        <Dropdown
          v-model="selected_role"
          :options="roles"
          optionLabel="type"
          placeholder="Pilih role"
          @change="changeRole()"
        />
        <small class="p-error" v-if="is_error.role">{{ error_msg.role }}</small>
      </div>
      <div class="p-field" v-if="warehouse_mandatory">
        <label>Warehouse</label>
        <Dropdown
          v-model="selected_warehouse"
          :options="warehouses"
          optionLabel="code"
          placeholder="Pilih warehouse"
          @change="is_error.warehouse_id = false"
        />
        <small class="p-error" v-if="is_error.warehouse_id">{{
          error_msg.warehouse_id
        }}</small>
      </div>
      <div class="p-field" v-if="outlet_mandatory">
        <label>Outlet</label>
        <Dropdown
          v-model="selected_outlet"
          :options="outlets"
          optionLabel="code"
          placeholder="Pilih outlet"
          @change="is_error.outlet_id = false"
        />
        <small class="p-error" v-if="is_error.outlet_id">{{
          error_msg.outlet_id
        }}</small>
      </div>
      <div class="p-field">
        <label>Nama</label>
        <InputText
          required
          v-model="create_data.name"
          placeholder="Nama"
          @keyup.prevent="is_error.name = false"
        />
        <small class="p-error" v-if="is_error.name">{{ error_msg.name }}</small>
      </div>
      <div class="p-field">
        <label>Email</label>
        <InputText
          type="email"
          required
          pattern=".+@globex\.com"
          v-model="create_data.email"
          placeholder="Email"
          @keyup.prevent="is_error.email = false"
        />
        <small class="p-error" v-if="is_error.email">{{
          error_msg.email
        }}</small>
      </div>
      <div class="p-field">
        <label>Phone</label>
        <InputText
          type="number"
          placeholder="Phone"
          v-model="create_data.phone"
          required
          @keyup.prevent="is_error.phone = false"
        />
        <small class="p-error" v-if="is_error.phone">{{
          error_msg.phone
        }}</small>
      </div>

      <template #footer>
        <Button
          label="Cancel"
          class="p-button-outlined"
          @click="update_dialog = false"
        />
        <Button
          label="Save"
          class="btn btn-primary"
          @click="updateEmployee()"
        />
      </template>
    </Dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex'

export default {
  computed: {
    ...mapState(['user', 'isAuthenticated'])
  },
  watch: {
    user() {
      this.getAuthUser()
    }
  },
  data() {
    return {
      cancel_dialog: false,
      create_dialog: false,
      update_dialog: false,
      loading_save: false,

      loading: {
        filter: false,
        create: false,
        delete: false
      },

      roles: [],
      selected_role: null,
      outlets: [],
      selected_outlet: null,
      warehouses: [],
      selected_warehouse: null,
      employees: [],
      search: {
        username: '',
        email: '',
        outlet_selected: '',
        warehouse_selected: ''
      },

      warehouse_mandatory: false,
      outlet_mandatory: false,
      password_field: false,

      create_data: {
        roles: [],
        warehouse_id: 0,
        outlet_id: 0,
        name: '',
        email: '',
        phone: '',
        password: ''
      },
      cancel_data: {},

      is_error: {
        role: false,
        warehouse_id: false,
        outlet_id: false,
        name: false,
        email: false,
        phone: false,
        password: false
      },
      error_msg: {
        role: '',
        warehouse_id: '',
        outlet_id: '',
        name: '',
        email: '',
        phone: '',
        password: ''
      },

      limit: 5,
      total_records: 0
    }
  },
  mounted() {
    this.getAuthUser()
    this.getEmployees()
    this.getRoles()
    this.getWarehouses()
    this.getOutlets()
  },
  methods: {
    getAuthUser() {
      console.info(this.user)
      console.info(this.user.outlet_id)
    },

    getRoles() {
      this.$http.get(`${process.env.VUE_APP_API_URL}/roles`).then(response => {
        if (response.status === 200) {
          this.roles = response.data.data.roles.filter(
            role => role.type !== 'CUSTOMER'
          )
        }
      })
    },

    getOutlets() {
      this.$http
        .get(`${process.env.VUE_APP_API_URL}/outlets`)
        .then(response => {
          if (response.status === 200) {
            // made code and name one line in this.outlets
            this.outlets = response.data.data.outlets.map(outlet => {
              return {
                code: `${outlet.code} - ${outlet.name}`,
                id: outlet.id
              }
            })
          }
        })
    },

    getWarehouses() {
      this.$http
        .get(`${process.env.VUE_APP_API_URL}/warehouses`)
        .then(response => {
          if (response.status === 200) {
            this.warehouses = response.data.data.warehouses
          }
        })
    },

    getEmployees(event) {
      let page = 1
      if (event) {
        page = event.page + 1
      }

      let out_id = ''
      if (this.search.outlet_selected) {
        out_id = this.search.outlet_selected.id
      } else {
        out_id = this.user.outlet_id
      }

      let warehouse_id = ''
      if (this.search.warehouse_selected) {
        warehouse_id = this.search.warehouse_selected.id
      }

      this.loading.filter = true
      this.$http
        .get(
          `${process.env.VUE_APP_API_URL}/employees?outlet_id=${out_id}&warehouse_id=${warehouse_id}&username=${this.search.username}&email=${this.search.email}&page=${page}&limit=${this.limit}`
        )
        .then(response => {
          this.loading.filter = false
          if (response.status === 200) {
            this.employees = response.data.data.employees
            this.total_records = response.data.data.total
          }
        })
    },

    createEmployee() {
      if (this.selected_role) {
        this.create_data.roles = [this.selected_role.type]
      } else {
        this.is_error.role = true
        this.error_msg.role = 'Mohon pilih role'
      }

      if (this.warehouse_mandatory) {
        if (this.selected_warehouse) {
          this.create_data.warehouse_id = this.selected_warehouse.id
        } else {
          this.is_error.warehouse_id = true
          this.error_msg.warehouse_id = 'Mohon pilih warehouse'
        }
      }

      if (this.outlet_mandatory) {
        if (this.selected_outlet) {
          this.create_data.outlet_id = this.selected_outlet.id
        } else {
          this.is_error.outlet_id = true
          this.error_msg.outlet_id = 'Mohon pilih outlet'
        }
      }

      if (!this.create_data.name) {
        this.is_error.name = true
        this.error_msg.name = 'Isi nama'
      }
      if (!this.create_data.email) {
        this.is_error.email = true
        this.error_msg.email = 'Isi email'
      }
      if (!this.create_data.phone) {
        this.is_error.phone = true
        this.error_msg.phone = 'Isi phone'
      }
      if (!this.create_data.password) {
        this.is_error.password = true
        this.error_msg.password = 'isi Password'
      }

      this.loading.create = true
      if (
        !this.is_error.role &&
        !this.is_error.name &&
        !this.is_error.email &&
        !this.is_error.phone &&
        !this.is_error.password
      ) {
        this.loading_save = true
        this.$http
          .post(`${process.env.VUE_APP_API_URL}/employees`, this.create_data)
          .then(response => {
            this.loading.create = false

            if (response.status === 200) {
              this.create_dialog = false
              this.getEmployees()
              this.resetCreateData()

              this.$toast.add({
                severity: 'success',
                summary: 'Berhasil menambahkan pegawai ',
                life: 3000
              })
              this.loading_save = false
            } else if (response.status === 400) {
              this.$toast.add({
                severity: 'error',
                summary: 'Email tidak valid',
                life: 3000
              })
              this.loading_save = false
            } else if (response.status === 500) {
              this.$toast.add({
                severity: 'error',
                summary: 'Email atau No telp sudah terdaftar',
                life: 3000
              })
              this.loading_save = false
            } else {
              this.$toast.add({
                severity: 'error',
                summary: 'Gagal menambahkan pegawai',
                life: 3000
              })
              this.loading_save = false
            }
          })
      }
    },

    resetCreateData() {
      this.selected_role = null
      this.selected_outlet = null
      this.selected_warehouse = null
      this.create_data = {
        roles: [],
        warehouse_id: 0,
        outlet_id: 0,
        name: '',
        email: '',
        phone: '',
        password: ''
      }
    },

    changeRole() {
      this.is_error.role = false
      if (this.user.role == 'SUPER_ADMIN') {
        if (this.selected_role.is_outlet) {
          this.outlet_mandatory = true
          this.warehouse_mandatory = false
          this.password_field = true
        }
        if (this.selected_role.is_warehouse) {
          this.warehouse_mandatory = true
          this.outlet_mandatory = false
          this.password_field = true
        }
      }

      if (
        (this.user.role == 'OUTLET_MANAGER') |
        (this.user.role == 'WAREHOUSE_MANAGER')
      ) {
        this.password_field = true
      }
      // if (this.selected_role.type == 'CUSTOMER') {
      //   this.password_field = false
      //   this.warehouse_mandatory = false
      //   this.outlet_mandatory = false
      // }
    },

    confirmCancel(shift) {
      this.cancel_data = shift
      this.cancel_dialog = true
    },

    confirmCancelUpdate(shift) {
      this.$http
        .get(
          `${process.env.VUE_APP_API_URL}/employee/detail?email=${shift.email}`
        )
        .then(response => {
          if (response.status === 200) {
            this.roles.forEach(role => {
              if ((response.data.data.roles || []).includes(role.type)) {
                this.selected_role = role
                this.selected_role.is_outlet = role.is_outlet
                this.selected_role.is_warehouse = role.is_warehouse
                this.changeRole()
              }
            })
            this.outlets.forEach(outlet => {
              if (outlet.id == response.data.data.outlet_id) {
                this.selected_outlet = outlet
              }
            })
            this.warehouses.forEach(warehouse => {
              if (warehouse.id == response.data.data.warehouse_id) {
                this.selected_warehouse = warehouse
              }
            })
            this.create_data = {
              name: response.data.data.name,
              email: response.data.data.email,
              phone: response.data.data.phone
            }
          }
        })
      this.update_dialog = true
    },

    updateEmployee() {
      if (this.selected_role) {
        this.create_data.roles = [this.selected_role.type]
      } else {
        this.is_error.role = true
        this.error_msg.role = 'Mohon pilih role'
      }

      if (this.warehouse_mandatory) {
        if (this.selected_warehouse) {
          this.create_data.warehouse_id = this.selected_warehouse.id
        } else {
          this.is_error.warehouse_id = true
          this.error_msg.warehouse_id = 'Mohon pilih warehouse'
        }
      }

      if (this.outlet_mandatory) {
        if (this.selected_outlet) {
          this.create_data.outlet_id = this.selected_outlet.id
        } else {
          this.is_error.outlet_id = true
          this.error_msg.outlet_id = 'Mohon pilih outlet'
        }
      }

      if (!this.create_data.name) {
        this.is_error.name = true
        this.error_msg.name = 'Isi nama'
      }
      if (!this.create_data.email) {
        this.is_error.email = true
        this.error_msg.email = 'Isi email'
      }
      if (!this.create_data.phone) {
        this.is_error.phone = true
        this.error_msg.phone = 'Isi phone'
      }

      this.loading.create = true
      if (
        !this.is_error.role &&
        !this.is_error.name &&
        !this.is_error.email &&
        !this.is_error.phone
      ) {
        this.$http
          .put(
            `${process.env.VUE_APP_API_URL}/employees/update?email=${this.create_data.email}`,
            this.create_data
          )
          .then(response => {
            if (response.status === 200) {
              this.getEmployees()
              this.$toast.add({
                severity: 'success',
                summary: 'Berhasil update employee',
                life: 3000
              })
              this.resetCreateData()
              this.update_dialog = false
            } else {
              this.$toast.add({
                severity: 'error',
                summary: 'Gagal update employee',
                life: 3000
              })
            }
          })
      }
    },

    cancelShift() {
      this.loading.cancel = true
      this.$http
        .patch(
          `${process.env.VUE_APP_API_URL}/employees/deactive?email=${this.cancel_data.email}`
        )
        .then(response => {
          this.loading.cancel = false
          this.cancel_dialog = false

          if (response.status === 200) {
            this.getEmployees()

            this.$toast.add({
              severity: 'success',
              summary: 'Berhasil menon-aktifkan employee',
              life: 3000
            })
          } else {
            this.$toast.add({
              severity: 'error',
              summary: 'Gagal menon-aktifkan employee',
              life: 3000
            })
          }
        })
    }
  }
}
</script>

<style scoped lang="scss">
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

::v-deep(.p-datatable table) {
  border-collapse: collapse;
}

::v-deep(.p-datatable th),
::v-deep(.p-datatable td) {
  border: 1px solid #ccc !important;
  padding: 8px;
}
</style>
